(function () {
  // Shoplift A/B: the new selector markup is rendered for everyone so both variants get identical
  // HTML from the server, and only variant B is activated. snippets/shoplift-api-live.liquid adds
  // the class and fires the event once Shoplift has resolved the bucket.
  const AB_CLASS = 'tlc-selector-b';
  const AB_EVENT = 'tlc:selector-ab';
  const isVariantB = () => document.documentElement.classList.contains(AB_CLASS);

  class VariantSteps {
    #touched = new WeakMap();
    #productIds = new WeakMap();

    constructor() {
      document.addEventListener("change", this.handleChange.bind(this));

      // The lock is otherwise only CSS (pointer-events: none), which does not stop a programmatic
      // click. sticky-add-to-cart.js:180 puppets this button with .click(), and Enter on a focused
      // button fires one too - either would add the default size without a choice being made.
      document.addEventListener("click", this.handleGatedClick.bind(this), true);
      document.addEventListener("variant:update", () => this.init());
      document.addEventListener(AB_EVENT, () => this.reset());
      this.init();
    }

    /** Re-runs from scratch when Shoplift resolves the bucket after first paint. */
    reset() {
      this.#touched = new WeakMap();
      this.#productIds = new WeakMap();

      // Resolved into the control after the pre-paint script already locked the button
      // (stale session bucket): hand the button back rather than leaving it gated.
      if (!isVariantB()) {
        this.#unlockAll();
        return;
      }

      this.init();
    }

    /** Clears every lock this class applied, so the control is left as the server rendered it. */
    #unlockAll() {
      document.querySelectorAll('.variant-steps-locked').forEach((button) => {
        button.classList.remove('variant-steps-locked');
        button.removeAttribute('aria-disabled');

        const label =
          button.querySelector('.add-to-cart-text__label') ||
          button.querySelector('.add-to-cart-text__content > span');
        if (label && button.dataset.variantStepsFallbackLabel) {
          label.textContent = button.dataset.variantStepsFallbackLabel;
        }
      });

      document.querySelectorAll('[data-variant-step].is-next').forEach((step) => {
        step.classList.remove('is-next');
      });
    }

    init() {
      if (!isVariantB()) return;
      document.querySelectorAll('variant-picker[data-template-product-match="true"]').forEach((picker) => {
        // Reset only on a real product switch (combined-listing colour); a size change can also morph the whole section.
        const productId = picker.dataset.productId;
        if (!this.#touched.has(picker) || this.#productIds.get(picker) !== productId) {
          this.#touched.set(picker, new Set());
          this.#productIds.set(picker, productId);
          this.#uncheckDefaults(picker);
        }
        this.render(picker);
      });
    }

    #uncheckDefaults(picker) {
      const stepsRoot = picker.querySelector("[data-variant-steps]");
      if (!stepsRoot) return;

      stepsRoot.querySelectorAll("[data-variant-step]:not([data-variant-step-static])").forEach((/** @type {Element} */ step) => {
        const name = step.getAttribute("data-variant-step-name");
        if (!name) return;

        const fieldset = picker.querySelector(`[data-option-name="${CSS.escape(name)}"]`);
        const checked = fieldset?.querySelector('input[type="radio"]:checked');
        if (checked instanceof HTMLInputElement) {
          checked.checked = false;
        }
      });
    }

    /** Blocks activation of a gated Add to cart from any source, not just a real pointer. */
    handleGatedClick(event) {
      if (!isVariantB()) return;

      const button = event.target instanceof Element ? event.target.closest('[ref="addToCartButton"]') : null;
      if (!button || !button.classList.contains("variant-steps-locked")) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    }

    handleChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLElement) || !input.matches('input[type="radio"], select')) return;

      const picker = input.closest('variant-picker[data-template-product-match="true"]');
      const optionName = input.closest("[data-option-name]")?.getAttribute("data-option-name");
      if (!picker || !optionName) return;

      const touched = this.#touched.get(picker);
      if (!touched) return;

      touched.add(optionName);
      this.render(picker);
    }

    render(picker) {
      const stepsRoot = picker.querySelector("[data-variant-steps]");
      const touched = this.#touched.get(picker) || new Set();

      if (stepsRoot) {
        stepsRoot.querySelectorAll("[data-variant-step]:not([data-variant-step-static])").forEach((/** @type {Element} */ step) => {
          const name = step.getAttribute("data-variant-step-name");
          const isComplete = !!name && touched.has(name);

          step.classList.toggle("is-complete", isComplete);
          step.classList.toggle("is-next", !isComplete);

          const status = step.querySelector("[data-variant-step-status]");
          if (status) {
            status.textContent = isComplete ? "Complete" : "Next";
          }
        });
      }

      this.updateGate(picker, stepsRoot, touched);
    }

    updateGate(picker, stepsRoot, touched) {
      if (!stepsRoot) return;

      const steps = Array.from(stepsRoot.querySelectorAll("[data-variant-step]:not([data-variant-step-static])"));
      const firstIncomplete = steps.find((step) => !touched.has(step.getAttribute("data-variant-step-name") || ""));
      const allComplete = !firstIncomplete;

      const section = picker.closest(".shopify-section");
      const button = (
        section?.querySelector('product-form-component [ref="addToCartButton"]')
      );
      if (!button) return;

      const wasLocked = button.classList.contains("variant-steps-locked");
      if (wasLocked === !allComplete) return;

      button.classList.toggle("variant-steps-locked", !allComplete);
      button.setAttribute("aria-disabled", String(!allComplete));

      const label = button.querySelector(".add-to-cart-text__label") || button.querySelector(".add-to-cart-text__content > span");
      if (!label) return;

      if (!allComplete) {
        const optionLabel = firstIncomplete?.getAttribute("data-variant-step-label") || "an option";
        label.textContent = `Choose ${optionLabel.toLowerCase()}`;
      } else {
        label.textContent = button.dataset.variantStepsFallbackLabel || "Add to cart";
      }
    }
  }

  // The loader lists this file in more than one branch; only ever instantiate once.
  if (!window.__tlcVariantSteps) window.__tlcVariantSteps = new VariantSteps();
})();
